import React, { useState } from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Typography, Tag, Table, Card, Space, Button, Modal, Form, Select, Input, message, Spin } from "antd";
const { TextArea } = Input;
import {
    FileTextOutlined,
    UndoOutlined,
    SyncOutlined,
    ShopOutlined,
    EditOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, Link, useParams } from "react-router-dom";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;

export const SaleShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "sales",
        id,
        queryOptions: {
            refetchOnMount: true,
            staleTime: 0,
        },
        meta: {
            fields: [
                "id",
                "invoiceNo",
                "customerTPIN",
                "customerId",
                "customerName",
                "salesStatusCode",
                "currencyTypeCode",
                "remark",
                "totalItemCount",
                "totalTaxableAmount",
                "totalTax",
                "totalAmount",
                "isActive",
                "status",
                "createdAt",
                "updatedAt",
                "documentUrl",
                {
                    items: [
                        "id",
                        "saleId",
                        "inventoryItemId",
                        "itemName",
                        "price",
                        "quantity",
                        "unitPrice",
                        "discountRate",
                        "discountAmount",
                        "totalAmount",
                        "taxRate",
                        "isActive",
                        {
                            inventoryItem: [
                                "id",
                                "itemCode",
                                "itemName",
                                "itemTypeCode",
                                "itemClassificationCode",
                                "barcode"
                            ]
                        }
                    ]
                },
                {
                    payments: [
                        "id",
                        "paymentMethod",
                        "amount",
                        "currencyTypeCode"
                    ]
                },
                {
                    taxes: [
                        "id",
                        "taxTypeCode",
                        "taxableAmount",
                        "taxPercent",
                        "taxAmount"
                    ]
                },
                {
                    billables: [
                        "id",
                        "saleId",
                        "description",
                        "customerName",
                        "customerContact",
                        "price",
                        "taxId",
                        "createdAt",
                        "updatedAt"
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    // Regeneration state
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PENDING":
                return "orange";
            case "COMPLETED":
                return "green";
            case "CANCELLED":
                return "red";
            default:
                return "default";
        }
    };

    const handleViewDocument = () => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        }
    };

    const handleInitiateReturn = () => {
        navigate(`/salereturns/create?invoiceNumber=${encodeURIComponent(record?.invoiceNo || '')}`);
    };

    // Helper function to display TPIN or manual entry indicator
    const displayCustomerTPIN = (tpin: string) => {
        if (!tpin || tpin === "0") {
            return <Text type="secondary" italic>Manual Entry</Text>;
        }
        return tpin;
    };

    const itemColumns = [
        {
            title: 'Item Code',
            dataIndex: 'itemCode',
            key: 'itemCode',
            render: (_: any, record: any) => {
                const itemCode = record?.inventoryItem?.itemCode || 'N/A';
                const inventoryItemId = record?.inventoryItemId;
                return inventoryItemId ? (
                    <Link to={`/products/show/${inventoryItemId}`} className="text-primary">
                        {itemCode}
                    </Link>
                ) : itemCode;
            }
        },
        {
            title: 'Item Name',
            dataIndex: 'itemName',
            key: 'itemName',
            render: (_: any, record: any) => {
                const itemName = record?.inventoryItem?.itemName || record?.itemName || 'N/A';
                const inventoryItemId = record?.inventoryItemId;
                return inventoryItemId ? (
                    <Link to={`/products/show/${inventoryItemId}`} className="text-primary">
                        {itemName}
                    </Link>
                ) : itemName;
            }
        },
        {
            title: 'Qty',
            dataIndex: 'quantity',
            key: 'quantity',
            render: (value: number) => value?.toFixed(2),
            responsive: ['md'] as const,
        },
        {
            title: 'Unit Price',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Discount',
            dataIndex: 'discountAmount',
            key: 'discountAmount',
            render: (value: number) => value?.toFixed(2),
            responsive: ['lg'] as const,
        },
        {
            title: 'Total',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (value: number) => <span className="font-semibold">{value?.toFixed(2)}</span>
        }
    ];

    const paymentColumns = [
        {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod'
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Currency',
            dataIndex: 'currencyTypeCode',
            key: 'currencyTypeCode'
        }
    ];

    const taxColumns = [
        {
            title: 'Tax Type',
            dataIndex: 'taxTypeCode',
            key: 'taxTypeCode'
        },
        {
            title: 'Taxable Amount',
            dataIndex: 'taxableAmount',
            key: 'taxableAmount',
            render: (value: number) => value?.toFixed(2)
        },
        {
            title: 'Rate',
            dataIndex: 'taxPercent',
            key: 'taxPercent',
            render: (value: number) => value?.toFixed(2) + '%'
        },
        {
            title: 'Tax Amount',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
            render: (value: number) => value?.toFixed(2)
        }
    ];

    const handleRegenerateSubmit = async (values: any) => {
        if (!record) return;

        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };

            const result = await invoiceRegenerationService.regenerateInvoice(record.id, request);

            if (result.success) {
                message.success(`Invoice ${record.invoiceNo} regenerated successfully!`);
                setRegenerateModalVisible(false);
                queryResult.refetch();
            } else {
                message.error(`Failed to regenerate invoice: ${result.errorMessage}`);
            }
        } catch (error: any) {
            console.error("Invoice regeneration error:", error);
            message.error(`Failed to regenerate invoice: ${error.response?.data?.message || error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const handleModalCancel = () => {
        setRegenerateModalVisible(false);
        form.resetFields();
    };

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const actions = [
        {
            label: "Edit",
            onClick: () => navigate(`/sales/edit/${id}`),
            type: "default" as const,
            icon: <EditOutlined />,
        },
    ];

    if (record?.documentUrl && record?.status === "COMPLETED") {
        actions.unshift({
            label: "Print",
            onClick: handleViewDocument,
            type: "primary" as const,
            icon: <FileTextOutlined />,
        });
    }

    const moreMenuItems = [];
    if (record?.documentUrl && record?.status === "COMPLETED") {
        moreMenuItems.push({
            key: "regenerate",
            label: "Regenerate Invoice",
            icon: <SyncOutlined />,
            onClick: () => setRegenerateModalVisible(true),
        });
        moreMenuItems.push({
            key: "return",
            label: "Return Sale",
            icon: <UndoOutlined />,
            onClick: handleInitiateReturn,
            danger: true,
        });
    }

    return (
        <div className="page-container">
            <PageHeader
                title={`Invoice #${record?.invoiceNo || ''}`}
                titleIcon={<ShopOutlined />}
                breadcrumbs={[
                    { title: "Sales", href: "/sales" },
                    { title: `Invoice #${record?.invoiceNo || ''}` },
                ]}
                actions={actions}
                moreMenuItems={moreMenuItems}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Total Amount</div>
                        <div className="mini-stat-value text-primary">
                            {record?.currencyTypeCode} {record?.totalAmount?.toFixed(2)}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.status === 'COMPLETED' ? 'active' : record?.status === 'PENDING' ? 'pending' : 'inactive'}`}>
                            {record?.status === 'COMPLETED' && <CheckCircleOutlined />}
                            {record?.status === 'PENDING' && <ClockCircleOutlined />}
                            {record?.status === 'CANCELLED' && <CloseCircleOutlined />}
                            {record?.status}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record?.currencyTypeCode}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Items</div>
                        <div className="mini-stat-value">{record?.totalItemCount || 0}</div>
                    </div>
                </div>
            </PageHeader>

            <div className="space-y-4">
                {/* Sale Information Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Sale Information</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Invoice No:</span>
                                <span className="info-value font-semibold">{record?.invoiceNo}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Status:</span>
                                <Tag color={getStatusColor(record?.status)}>{record?.status}</Tag>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Customer:</span>
                                <span className="info-value">
                                    {record?.customerId ? (
                                        <Link to={`/customers/show/${record.customerId}`} className="text-primary">
                                            {record?.customerName}
                                        </Link>
                                    ) : (
                                        record?.customerName
                                    )}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Customer TPIN:</span>
                                <span className="info-value">{displayCustomerTPIN(record?.customerTPIN)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Currency:</span>
                                <span className="info-value">{record?.currencyTypeCode}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Created At:</span>
                                <span className="info-value">
                                    <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                </span>
                            </div>
                            {record?.remark && (
                                <div className="info-item full-width">
                                    <span className="info-label">Remark:</span>
                                    <span className="info-value">{record.remark}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Billable Services */}
                {record?.billables && record.billables.length > 0 && (
                    <Card title="Billable Services" className="data-table-card">
                        <Table
                            dataSource={record.billables}
                            rowKey={(rec) => rec.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 600 }}
                            columns={[
                                {
                                    title: 'Description',
                                    dataIndex: 'description',
                                    key: 'description'
                                },
                                {
                                    title: 'Customer',
                                    dataIndex: 'customerName',
                                    key: 'customerName',
                                    responsive: ['md'] as const,
                                },
                                {
                                    title: 'Contact',
                                    dataIndex: 'customerContact',
                                    key: 'customerContact',
                                    responsive: ['lg'] as const,
                                },
                                {
                                    title: 'Price',
                                    dataIndex: 'price',
                                    key: 'price',
                                    render: (value: number) => <span className="font-semibold">{value?.toFixed(2)}</span>
                                }
                            ]}
                        />
                    </Card>
                )}

                {/* Sale Items */}
                <Card title={`Sale Items (${record?.items?.length || 0})`} className="data-table-card">
                    {record?.items && record.items.length > 0 ? (
                        <Table
                            dataSource={record.items}
                            columns={itemColumns}
                            rowKey={(rec) => rec.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 800 }}
                        />
                    ) : (
                        <div className="text-center py-8 text-text-muted">
                            {record?.billables && record.billables.length > 0
                                ? "No physical items - This sale contains only billable services"
                                : "No sale items available"}
                        </div>
                    )}
                </Card>

                {/* Taxes */}
                {record?.taxes && record.taxes.length > 0 && (
                    <Card title="Taxes" className="data-table-card">
                        <Table
                            dataSource={record.taxes}
                            columns={taxColumns}
                            rowKey={(rec) => rec.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 500 }}
                        />
                    </Card>
                )}

                {/* Payments */}
                {record?.payments && record.payments.length > 0 && (
                    <Card title="Payments" className="data-table-card">
                        <Table
                            dataSource={record.payments}
                            columns={paymentColumns}
                            rowKey={(rec) => rec.id}
                            pagination={false}
                            className="custom-table"
                            scroll={{ x: 400 }}
                        />
                    </Card>
                )}

                {/* Summary Card */}
                <div className="info-card">
                    <div className="info-card-header">
                        <h3 className="info-card-title">Summary</h3>
                    </div>
                    <div className="info-card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Total Items:</span>
                                <span className="info-value">{record?.totalItemCount}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Taxable Amount:</span>
                                <span className="info-value">{record?.totalTaxableAmount?.toFixed(2)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Tax:</span>
                                <span className="info-value">{record?.totalTax?.toFixed(2)}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value font-semibold text-primary text-lg">
                                    {record?.currencyTypeCode} {record?.totalAmount?.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Regenerate Modal */}
            <Modal
                title="Regenerate Invoice"
                open={regenerateModalVisible}
                onCancel={handleModalCancel}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRegenerateSubmit}
                >
                    <Form.Item
                        name="regenerationType"
                        label="Regeneration Type"
                        rules={[{ required: true, message: 'Please select a regeneration type' }]}
                    >
                        <Select placeholder="Select regeneration type">
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="DataCorrection">Data Correction</Select.Option>
                            <Select.Option value="FormatUpdate">Format Update</Select.Option>
                            <Select.Option value="CustomerRequest">Customer Request</Select.Option>
                            <Select.Option value="Other">Other</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reason"
                        label="Reason"
                        rules={[{ required: true, message: 'Please provide a reason' }]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Enter reason for regeneration..."
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>
                                Regenerate
                            </Button>
                            <Button onClick={handleModalCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
